import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  FileText, 
  Search,
  Filter,
  RotateCcw,
  Plus,
  X,
  Settings,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { shipmentService } from '../services/apiService';
import { SHIPMENT_FIELDS, USER_TYPES } from '../constants';
import { getCurrentMonthRange, formatDateForDisplay } from '../utils/dateUtils';


const calculateTotalEarnings = (shipments) => {
  return shipments.reduce((total, shipment) => {
    const chiphi = parseFloat(shipment.chiphi || 0);
    const chiphi1 = parseFloat(shipment.chiphi1 || 0);
    const chiphi2 = parseFloat(shipment.chiphi2 || 0);
    const chiphi3 = parseFloat(shipment.chiphi3 || 0);
    const chiphi4 = parseFloat(shipment.chiphi4 || 0);
    return total + chiphi + chiphi1 + chiphi2 + chiphi3 + chiphi4;
  }, 0);
};

// Helper function to get summary stats
const getSummaryStats = (shipments) => {
  const totalTrips = shipments.length;
  const totalEarnings = calculateTotalEarnings(shipments);
  
  // Count by status
  const statusCounts = shipments.reduce((acc, shipment) => {
    const status = shipment.trangthai;
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  return {
    totalTrips,
    totalEarnings,
    statusCounts,
    completedTrips: statusCounts[5] || 0, // Status 5 = Hoàn thành
    inProgressTrips: (statusCounts[1] || 0) + (statusCounts[2] || 0), // Status 1,2 = Đang thực hiện
  };
};


// Helper function to calculate driver earnings
const calculateDriverEarnings = (shipment) => {
  const chiphi = parseFloat(shipment.chiphi || 0);
  const chiphi1 = parseFloat(shipment.chiphi1 || 0);
  const chiphi2 = parseFloat(shipment.chiphi2 || 0);
  const chiphi3 = parseFloat(shipment.chiphi3 || 0);
  const chiphi4 = parseFloat(shipment.chiphi4 || 0);
  return chiphi + chiphi1 + chiphi2 + chiphi3 + chiphi4;
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === 0) return '0đ';
  return new Intl.NumberFormat('vi-VN').format(amount) + 'đ';
};

// Helper function to get pickup and delivery info
const getPickupDeliveryInfo = (shipment) => {
  const pickupParts = [];
  const deliveryParts = [];
  
  // === PICKUP (Lấy hàng) ===
  if (shipment.diachinhan && shipment.diachinhan.trim()) {
    pickupParts.push(shipment.diachinhan.trim());
  } else if (shipment.khachhangnhan && shipment.khachhangnhan.trim()) {
    // Nếu không có địa chỉ cụ thể, dùng tên khách hàng nhận
    pickupParts.push(shipment.khachhangnhan.trim());
  }
  
  // === DELIVERY (Giao hàng) ===
  for (let i = 1; i <= 4; i++) {
    const address = shipment[`diachigiao${i}`];
    const customer = shipment[`khachhang${i}`];
    
    if (address && address.trim()) {
      deliveryParts.push(address.trim());
    } else if (customer && customer.trim()) {
      // Nếu không có địa chỉ cụ thể, dùng tên khách hàng
      deliveryParts.push(customer.trim());
    }
  }
  
  // Format output
  const pickup = pickupParts.length > 0 ? pickupParts[0] : 'N/A';
  const delivery = deliveryParts.length > 0 ? 
    deliveryParts.join(', ') + (deliveryParts.length > 1 ? ` (+${deliveryParts.length - 1} điểm)` : '') 
    : 'N/A';
  
  return {
    pickup: pickup.length > 30 ? pickup.substring(0, 30) + '...' : pickup,
    delivery: delivery.length > 50 ? delivery.substring(0, 50) + '...' : delivery
  };
};

// Filter Popup Component
const FilterPopup = ({ 
  isOpen, 
  onClose, 
  filters, 
  onFiltersChange,
  onApplyFilters,
  onClearFilters 
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters = {
      fromDate: localFilters.fromDate,
      toDate: localFilters.toDate,
      searchTerm: '',
      statusFilter: 'all'
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] overflow-auto">
        {/* Popup Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center">
            <Filter className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Bộ lọc</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Popup Content */}
        <div className="p-4 space-y-4">
          {/* Date Range */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Khoảng thời gian</h4>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Từ ngày</label>
                <input
                  type="date"
                  value={localFilters.fromDate}
                  onChange={(e) => setLocalFilters(prev => ({ ...prev, fromDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Đến ngày</label>
                <input
                  type="date"
                  value={localFilters.toDate}
                  onChange={(e) => setLocalFilters(prev => ({ ...prev, toDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>

          {/* Search */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Tìm kiếm</h4>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Tài xế, biển số, sdt, địa chỉ..."
                value={localFilters.searchTerm}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                className="w-full pl-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Trạng thái</h4>
            <select
              value={localFilters.statusFilter}
              onChange={(e) => setLocalFilters(prev => ({ ...prev, statusFilter: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="0">Chờ xác nhận</option>
              <option value="1">Đã nhận chuyến</option>
              <option value="2">Đang giao</option>
              <option value="5">Hoàn thành</option>
              <option value="9">Hủy</option>
            </select>
          </div>

          {/* Filter Preview */}
          {(localFilters.searchTerm || localFilters.statusFilter !== 'all') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-sm text-blue-700">
                <span className="font-medium">Bộ lọc sẽ áp dụng:</span>
                {localFilters.searchTerm && (
                  <div className="mt-1">• Tìm kiếm: "{localFilters.searchTerm}"</div>
                )}
                {localFilters.statusFilter !== 'all' && (
                  <div className="mt-1">
                    • Trạng thái: {
                      localFilters.statusFilter === '0' ? 'Chờ xác nhận' :
                      localFilters.statusFilter === '1' ? 'Đã nhận chuyến' :
                      localFilters.statusFilter === '2' ? 'Đang giao' :
                      localFilters.statusFilter === '5' ? 'Hoàn thành' :
                      localFilters.statusFilter === '9' ? 'Hủy' : localFilters.statusFilter
                    }
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Popup Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClear}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Xóa bộ lọc
          </button>
          
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
            >
              Hủy
            </button>
            <button
              onClick={handleApply}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Áp dụng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShipmentReport = ({ userData, onBack, onShipmentClick, onCreateShipment }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [shipments, setShipments] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(
  userData?.type === USER_TYPES.DRIVER ? '5' : 'all' // Default to completed for driver
);
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Initialize date range to current month
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setFromDate(formatDateForInput(firstDay));
    setToDate(formatDateForInput(lastDay));
  }, []);

  const toggleRowExpansion = (index) => {
  const newExpanded = new Set(expandedRows);
  if (newExpanded.has(index)) {
    newExpanded.delete(index);
  } else {
    newExpanded.add(index);
  }
  setExpandedRows(newExpanded);
};

const getDetailedEarnings = (shipment) => {
  const chiphi = parseFloat(shipment.chiphi || 0);
  const chiphi1 = parseFloat(shipment.chiphi1 || 0);
  const chiphi2 = parseFloat(shipment.chiphi2 || 0);
  const chiphi3 = parseFloat(shipment.chiphi3 || 0);
  const chiphi4 = parseFloat(shipment.chiphi4 || 0);
  
  return {
    chiphi,
    chiphi1,
    chiphi2,
    chiphi3,
    chiphi4,
    total: chiphi + chiphi1 + chiphi2 + chiphi3 + chiphi4
  };
};

const formatCurrencyDetailed = (amount) => {
  if (amount === 0) return '0đ';
  return new Intl.NumberFormat('vi-VN').format(amount) + 'đ';
};


  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDateForAPI = (dateString) => {
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };

  useEffect(() => {
    if (fromDate && toDate) {
      loadShipments();
    }
  }, [fromDate, toDate, userData]);

  useEffect(() => {
    filterShipments();
  }, [shipments, searchTerm, statusFilter]);

  const loadShipments = async () => {
    if (!userData?.idLaiXe && userData?.idLaiXe !== 0) return;
    if (!fromDate || !toDate) return;
    
    setLoading(true);
    setError('');
    
    try {
      const tungay = formatDateForAPI(fromDate);
      const denngay = formatDateForAPI(toDate);
      
      const result = await shipmentService.getShipmentsForDriver(
        userData?.type === 2 ? 0 : userData.idLaiXe, // 0 for dispatcher to get all drivers
        tungay,
        denngay
      );
      
      if (result.success && result.data) {
        // Process and sort shipments
        const processedShipments = result.data.map(shipment => ({
          ...shipment,
          earnings: calculateDriverEarnings(shipment),
          pickupDelivery: getPickupDeliveryInfo(shipment)
        }));
        
        setShipments(processedShipments);
      } else {
        setError(result.message || 'Không thể tải dữ liệu chuyến xe');
        setShipments([]);
      }
    } catch (err) {
      setError('Có lỗi xảy ra khi tải dữ liệu');
      setShipments([]);
    } finally {
      setLoading(false);
    }
  };

  const filterShipments = () => {
    let filtered = [...shipments];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(shipment => 
        (shipment[SHIPMENT_FIELDS.DRIVER_NAME] || '').toLowerCase().includes(term) ||
        (shipment[SHIPMENT_FIELDS.VEHICLE_NUMBER] || '').toLowerCase().includes(term) ||
        (shipment[SHIPMENT_FIELDS.DRIVER_PHONE] || '').toLowerCase().includes(term) ||
        (shipment.pickupDelivery.pickup || '').toLowerCase().includes(term) ||
        (shipment.pickupDelivery.delivery || '').toLowerCase().includes(term)
      );
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(shipment => 
        shipment[SHIPMENT_FIELDS.STATUS] === parseInt(statusFilter)
      );
    }
    
    // Sort filtered results
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.field) {
        case 'driver':
          aValue = a[SHIPMENT_FIELDS.DRIVER_NAME] || '';
          bValue = b[SHIPMENT_FIELDS.DRIVER_NAME] || '';
          break;
        case 'date':
          aValue = new Date(a[SHIPMENT_FIELDS.DELIVERY_DATE].split('-').reverse().join('-'));
          bValue = new Date(b[SHIPMENT_FIELDS.DELIVERY_DATE].split('-').reverse().join('-'));
          break;
        case 'earnings':
          aValue = a.earnings;
          bValue = b.earnings;
          break;
        case 'status':
          aValue = a[SHIPMENT_FIELDS.STATUS];
          bValue = b[SHIPMENT_FIELDS.STATUS];
          break;
        case 'trocapchuyen':
          aValue = parseFloat(a[SHIPMENT_FIELDS.TRO_CAP_CHUYEN] || 0);
          bValue = parseFloat(b[SHIPMENT_FIELDS.TRO_CAP_CHUYEN] || 0);
          break;
        default:
          aValue = '';
          bValue = '';
      }

      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredShipments(filtered);
  };

  const handleShipmentClick = (shipment) => {
    if (onShipmentClick && shipment[SHIPMENT_FIELDS.ID]) {
      console.log('Clicked on shipment:', shipment[SHIPMENT_FIELDS.ID]);
      onShipmentClick(shipment[SHIPMENT_FIELDS.ID]);
    }
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const handleApplyFilters = (filters) => {
    setFromDate(filters.fromDate);
    setToDate(filters.toDate);
    setSearchTerm(filters.searchTerm);
    setStatusFilter(filters.statusFilter);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getSortIcon = (field) => {
    if (sortConfig.field !== field) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            {/* Create Shipment Button - Always visible for Dispatcher */}
            {userData?.type === USER_TYPES.DISPATCHER && onCreateShipment && (
              <button
                onClick={onCreateShipment}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                <span className="font-medium">Tạo chuyến</span>
              </button>
            )}

            {/* Filter Button */}
            <button
              onClick={() => setShowFilterPopup(true)}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors shadow-sm ${
                hasActiveFilters 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              <span className="font-medium">Bộ lọc</span>
              {hasActiveFilters && (
                <span className="ml-2 bg-white text-blue-600 text-xs px-2 py-1 rounded-full font-bold">
                  !
                </span>
              )}
            </button>
          </div>

          {/* Active Filter Summary */}
          {
          userData?.type === USER_TYPES.DRIVER &&
          !loading && !error && filteredShipments.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                {/* Tổng số chuyến */}
                <div>
                  <div className="text-lg font-bold text-gray-800">
                    {getSummaryStats(filteredShipments).totalTrips}
                  </div>
                  <div className="text-sm text-gray-600">Tổng số chuyến</div>
                </div>

                {/* Tổng số tiền */}
                <div>
                  <div className="text-lg font-bold text-gray-800">
                    {formatCurrency(getSummaryStats(filteredShipments).totalEarnings)}
                  </div>
                  <div className="text-sm text-gray-600">Tổng thu nhập</div>
                </div>
              </div>
            </div>
          )}
          {hasActiveFilters && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-blue-700">
                  <span className="font-medium">Bộ lọc đang áp dụng:</span>
                  {searchTerm && <span className="ml-2">Tìm kiếm: "{searchTerm}"</span>}
                  {statusFilter !== 'all' && (
                    <span className="ml-2">
                      Trạng thái: {
                        statusFilter === '0' ? 'Chờ xác nhận' :
                        statusFilter === '1' ? 'Đã nhận chuyến' :
                        statusFilter === '2' ? 'Đang giao' :
                        statusFilter === '5' ? 'Hoàn thành' :
                        statusFilter === '9' ? 'Hủy' : statusFilter
                      }
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm text-blue-600 font-medium">
                    {filteredShipments.length} / {shipments.length} chuyến xe
                  </div>
                  <button
                    onClick={clearFilters}
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Xóa
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
 
      {/* Content Area */}
      <div>
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Đang tải dữ liệu...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="p-4">
            <div className="bg-red-50 text-red-600 p-4 rounded-lg text-center">
              <p className="font-medium">{error}</p>
              <button
                onClick={loadShipments}
                className="mt-3 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Thử lại
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div className="p-4">
            {filteredShipments.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th 
                          className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('date')}
                        >
                          <div className="flex items-center">
                            Ngày
                            <span className="ml-1">{getSortIcon('date')}</span>
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('driver')}
                        >
                          <div className="flex items-center">
                            Biển số
                            <span className="ml-1">{getSortIcon('driver')}</span>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          Nhận - giao
                        </th>
                        <th
                          className="px-4 py-3 text-center text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                          onClick={() => handleSort('trocapchuyen')}
                        >
                          <div className="flex items-center justify-center">
                            Trợ cấp chuyến
                            <span className="ml-1">{getSortIcon('trocapchuyen')}</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
  {filteredShipments.map((shipment, index) => {
    const isExpanded = expandedRows.has(index);
    const earnings = getDetailedEarnings(shipment);
    const isDriver = userData?.type === USER_TYPES.DRIVER;
    
    return (
      <React.Fragment key={index}>
        {/* Main Row */}
        <tr 
          className="hover:bg-blue-50 cursor-pointer transition-colors"
          onClick={() => {
            if (isDriver) {
              toggleRowExpansion(index);
            } else {
              handleShipmentClick(shipment);
            }
          }}
        >
          <td className="px-4 py-3">
            <div className="text-sm font-medium text-gray-900">
              {shipment[SHIPMENT_FIELDS.DELIVERY_DATE] || 'N/A'}
            </div>
          </td>
          <td className="px-4 py-3">
            {userData?.type === USER_TYPES.DISPATCHER ?   <div className="text-sm font-medium text-gray-900">
              {shipment[SHIPMENT_FIELDS.DRIVER_NAME] || 'N/A'}
            </div> : null}
            <div className="text-sm font-bold text-blue-600 mt-1">
              {shipment[SHIPMENT_FIELDS.VEHICLE_NUMBER] || 'N/A'}
            </div>
            {userData?.type === USER_TYPES.DISPATCHER ?   <div className="text-xs text-gray-500 mt-1">
              {shipment[SHIPMENT_FIELDS.DRIVER_PHONE] || ''}
            </div> : null}
          </td>
           <td className="px-4 py-3">
                          <div className="text-sm">
                            <div className="text-gray-900 mb-1">
                              <span className="text-orange-600 font-medium">Lấy:</span> {shipment.pickupDelivery.pickup}
                            </div>
                            <div className="text-gray-900">
                              <span className="text-green-600 font-medium">Giao:</span> {shipment.pickupDelivery.delivery}
                            </div>
                          </div>
                        </td>
          <td className="px-4 py-3 text-center">
            <div className="text-sm font-bold text-green-600">
              {formatCurrencyDetailed(parseFloat(shipment[SHIPMENT_FIELDS.TRO_CAP_CHUYEN] || 0))}
            </div>
          </td>
        </tr>
        
        {/* Expanded Row - Chi phí chi tiết (chỉ cho driver) */}
        {isDriver && isExpanded && (
          <tr className="bg-gray-50 border-t border-gray-200">
            <td colSpan="4" className="px-4 py-3">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Chi tiết chi phí</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {earnings.chiphi > 0 && (
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Chi phí chính</div>
                      <div className="text-sm font-bold text-green-600">
                        {formatCurrencyDetailed(earnings.chiphi)}
                      </div>
                    </div>
                  )}
                  {earnings.chiphi1 > 0 && (
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Chi phí 1</div>
                      <div className="text-sm font-bold text-green-600">
                        {formatCurrencyDetailed(earnings.chiphi1)}
                      </div>
                    </div>
                  )}
                  {earnings.chiphi2 > 0 && (
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Chi phí 2</div>
                      <div className="text-sm font-bold text-green-600">
                        {formatCurrencyDetailed(earnings.chiphi2)}
                      </div>
                    </div>
                  )}
                  {earnings.chiphi3 > 0 && (
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Chi phí 3</div>
                      <div className="text-sm font-bold text-green-600">
                        {formatCurrencyDetailed(earnings.chiphi3)}
                      </div>
                    </div>
                  )}
                  {earnings.chiphi4 > 0 && (
                    <div className="text-center">
                      <div className="text-xs text-gray-500">Chi phí 4</div>
                      <div className="text-sm font-bold text-green-600">
                        {formatCurrencyDetailed(earnings.chiphi4)}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Tổng chi phí */}
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Tổng thu nhập:</span>
                    <span className="text-lg font-bold text-green-700">
                      {formatCurrencyDetailed(earnings.total)}
                    </span>
                  </div>
                </div>
                
                {/* Action buttons cho driver */}
                <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => handleShipmentClick(shipment)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    Xem chi tiết chuyến
                  </button>
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  })}
</tbody>

                  </table>
                </div>
                
                {/* Pagination info */}
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                  <div className="text-sm text-gray-700 text-center">
                    Hiển thị {filteredShipments.length} chuyến xe
                    {hasActiveFilters && ` (đã lọc từ ${shipments.length} chuyến)`}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                <div className="text-gray-300 mb-6">
                  <FileText className="w-20 h-20 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
  {(searchTerm || statusFilter !== 'all') ? 'Không tìm thấy kết quả' : 
   userData?.type === USER_TYPES.DRIVER ? 'Chưa có chuyến xe hoàn thành' : 'Không có dữ liệu'}
</h3>
<p className="text-gray-500">
  {(searchTerm || statusFilter !== 'all') 
    ? `Không tìm thấy chuyến xe nào phù hợp với bộ lọc`
    : userData?.type === USER_TYPES.DRIVER 
      ? `Bạn chưa có chuyến xe hoàn thành nào trong khoảng thời gian đã chọn`
      : `Không có chuyến xe nào trong khoảng thời gian đã chọn`
  }
</p>
               
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Xóa bộ lọc
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter Popup */}
      <FilterPopup
        isOpen={showFilterPopup}
        onClose={() => setShowFilterPopup(false)}
        filters={{
          fromDate,
          toDate,
          searchTerm,
          statusFilter
        }}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
};

export default ShipmentReport;
